import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Calendar, Pencil, X, Plus, ArrowLeft } from "lucide-react";
import Swal from 'sweetalert2';
import { LuNotepadText } from "react-icons/lu";
import JoditEditor from 'jodit-react';

function ContentDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const API_BASE_URL = "https://backend.dlapakistan.com/api/member/";
  const emerald600 = "#059669";

  // Check if this is a new content page (from query params)
  const queryParams = new URLSearchParams(location.search);
  const isNewContent = queryParams.get('button') !== null;
  const buttonName = queryParams.get('button') || '';
  const suggestedSlug = queryParams.get('slug') || '';

  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: ""
  });
  const [saving, setSaving] = useState(false);

  // Add Content modal state (only for new content)
  const [showAddContentModal, setShowAddContentModal] = useState(false);
  const [newContentForm, setNewContentForm] = useState({
    button: buttonName,
    title: "",
    description: ""
  });
  const [newContentFormErrors, setNewContentFormErrors] = useState({
    button: false,
    title: false,
    description: false
  });
  const [addingContent, setAddingContent] = useState(false);

  // Jodit Editor Refs and Config
  const editEditorRef = useRef(null);
  const addEditorRef = useRef(null);

  // Jodit Editor Configuration - memoized to prevent re-renders
  const editorConfig = useMemo(() => ({
    readonly: false,
    height: 300,
    theme: "default",
    toolbarButtonSize: "medium",
    removeButtons: ['source', 'fullsize', 'about', 'print', 'preview', 'find'],
    buttons: [
      'bold', 'italic', 'underline', 'strikethrough',
      'ul', 'ol', 'outdent', 'indent',
      'font', 'fontsize', 'brush', 'paragraph',
      'image', 'table', 'link', 'align',
      'undo', 'redo', 'hr', 'eraser', 'copyformat',
      'selectall'
    ],
    style: {
      background: '#f9fafb',
    },
    uploader: {
      insertImageAsBase64URI: true
    },
    placeholder: 'Description here...',
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    disablePlugins: ['paste', 'pasteStorage'],
  }), []);

  useEffect(() => {
    if (isNewContent) {
      // This is a new content page
      setIsLoading(false);
    } else {
      // This is an existing content page, load the content
      loadContent();
    }
  }, [slug, isNewContent]);

  const loadContent = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}contents/by-slug/${slug}/`);
      
      if (!response.ok) {
        const allResponse = await fetch(`${API_BASE_URL}contents/`);
        if (!allResponse.ok) throw new Error('Failed to fetch content');
        
        const allContents = await allResponse.json();
        const foundContent = allContents.find(c => c.slug === slug);
        
        if (foundContent) {
          setContent(foundContent);
          setEditForm({
            title: foundContent.title,
            description: foundContent.description
          });
        } else {
          setError("Content not found");
        }
      } else {
        const foundContent = await response.json();
        setContent(foundContent);
        setEditForm({
          title: foundContent.title,
          description: foundContent.description
        });
      }
    } catch (err) {
      console.error("Error loading content:", err);
      setError("Failed to load content");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? "Invalid date" : 
      date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };

  // Handle edit form changes
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle Jodit Editor change for edit form
  const handleEditEditorChange = (newContent) => {
    setEditForm(prev => ({
      ...prev,
      description: newContent
    }));
  };

  // Handle new content form changes
  const handleNewContentChange = (e) => {
    const { name, value } = e.target;
    setNewContentForm(prev => ({ ...prev, [name]: value }));
    if (value.trim()) setNewContentFormErrors(prev => ({ ...prev, [name]: false }));
  };

  // Handle Jodit Editor change for new content form
  const handleNewContentEditorChange = (newContent) => {
    setNewContentForm(prev => ({
      ...prev,
      description: newContent
    }));
    if (newContent.trim()) setNewContentFormErrors(prev => ({ ...prev, description: false }));
  };

  // Generate slug from button name
  const generateSlug = (buttonName) => {
    if (!buttonName) return "";
    return buttonName.toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
      .trim();
  };

  // Save edited content
  const handleSaveEdit = async () => {
    if (!editForm.title.trim() || !editForm.description.trim()) {
      Swal.fire({
        title: "Required Fields",
        text: "Title and description are required",
        icon: "warning"
      });
      return;
    }

    try {
      setSaving(true);
      const response = await fetch(`${API_BASE_URL}contents/${content.id}/`, {
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: editForm.title,
          description: editForm.description
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Update failed: ${errorText}`);
      }

      const updatedContent = await response.json();
      setContent(updatedContent);
      
      Swal.fire({
        title: "Success!",
        text: "Content updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false
      });
      
      setShowEditModal(false);
    } catch (err) {
      console.error("Update error:", err);
      Swal.fire({
        title: "Error",
        text: `Failed to update content: ${err.message}`,
        icon: "error"
      });
    } finally {
      setSaving(false);
    }
  };

  // Add new content
  const handleAddContent = async () => {
    // Validate required fields
    let hasErrors = false;
    const newErrors = { button: false, title: false, description: false };

    if (!newContentForm.button.trim()) { newErrors.button = true; hasErrors = true; }
    if (!newContentForm.title.trim()) { newErrors.title = true; hasErrors = true; }
    if (!newContentForm.description.trim()) { newErrors.description = true; hasErrors = true; }

    setNewContentFormErrors(newErrors);
    if (hasErrors) {
      Swal.fire({
        title: "Required Fields Missing",
        html: `<div class="text-left"><p class="mb-2">Please fill in:</p><ul class="list-disc pl-5">
          ${newErrors.button ? "<li><strong>Button Name</strong></li>" : ""}
          ${newErrors.title ? "<li><strong>Title</strong></li>" : ""}
          ${newErrors.description ? "<li><strong>Description</strong></li>" : ""}</ul></div>`,
        icon: "warning",
        confirmButtonColor: emerald600,
      });
      return;
    }

    try {
      setAddingContent(true);
      
      const response = await fetch(`${API_BASE_URL}contents/`, {
        method: "POST",
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          button: newContentForm.button,
          title: newContentForm.title,
          description: newContentForm.description,
          slug: generateSlug(newContentForm.button)
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const newContent = await response.json();
      
      // Reset form
      closeAddContentModal();
      
      Swal.fire({
        title: "Success!",
        text: "Content added successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Navigate to the new content page
      navigate(`/About-Dla/BecomeMember/content/${newContent.slug}`);
      
    } catch (err) {
      console.error("Failed to add content:", err);
      Swal.fire({ 
        title: "Error", 
        text: `Failed to add content: ${err.message}`, 
        icon: "error" 
      });
    } finally {
      setAddingContent(false);
    }
  };

  // Close add content modal
  const closeAddContentModal = () => {
    setShowAddContentModal(false);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen pt-32">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600"></div>
        <p className="mt-4 text-gray-600">Loading content...</p>
      </div>
    );
  }

  if (error && !isNewContent) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 pt-48">
        <div className="text-red-500 text-2xl mb-4">⚠️</div>
        <p className="text-gray-600 text-xl mb-4">{error}</p>
        <button onClick={() => navigate("/About-Dla/BecomeMember")} className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors">
          Back to Posts
        </button>
      </div>
    );
  }

  // If this is a new content page, show different UI
  if (isNewContent) {
    return (
      <div className="flex flex-col min-h-screen w-full bg-gray-50 pt-16"> {/* Changed: pt-16 instead of mt-20 */}
        {/* Header Section */}
        <div className="px-8 py-6 bg-white border-b">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="text-left">
              <h1 className="text-3xl font-bold text-gray-900">Add New Content</h1>
              <p className="text-gray-600 mt-1">Create content for button: <strong>{buttonName}</strong></p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate("/About-Dla/BecomeMember")}
                className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors gap-2"
              >
                <ArrowLeft className="w-5 h-5" /> Back to Posts
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-8 md:px-12 py-8 w-full flex-grow"> {/* Changed: py-8 instead of mt-8 */}
          <div className="border-2 border-dashed border-gray-300 rounded-xl bg-white shadow-sm h-full">
            <div className="p-8 h-full">
              <div className="text-center py-12 h-full flex flex-col justify-center">
                {/* Icon with increased size */}
                <div className="mb-6 flex justify-center">
                  <LuNotepadText className="text-8xl text-gray-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-600 mb-4">Add Content for "{buttonName}"</h2>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">
                  You are adding content for the button "<strong className="text-emerald-600">{buttonName}</strong>".
                  Fill in the details below to create the content.
                </p>
                
                <div className="mt-4">
                  <button
                    onClick={() => setShowAddContentModal(true)}
                    className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors flex items-center gap-2 mx-auto"
                  >
                    <Plus className="w-5 h-5" /> Start Adding Content
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add Content Modal - Opens only when user clicks button */}
        {showAddContentModal && (
          <>
            <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeAddContentModal} />
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
              <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl pointer-events-auto animate-modalIn border border-gray-100" onClick={(e) => e.stopPropagation()}>
                <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                  <h2 className="text-xl font-bold text-gray-800">Add Content for "{buttonName}"</h2>
                  <button onClick={closeAddContentModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                    <X size={20} />
                  </button>
                </div>
                <div className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                  {/* BUTTON NAME FIELD (READ-ONLY) */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Button Name
                    </label>
                    <input 
                      name="button" 
                      value={newContentForm.button} 
                      readOnly
                      className="w-full px-5 py-3 border border-gray-300 rounded-2xl bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      This is the button you clicked in the post. It cannot be changed.
                    </p>
                  </div>

                  {/* TITLE FIELD */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input 
                      name="title" 
                      value={newContentForm.title} 
                      onChange={handleNewContentChange} 
                      className={`w-full px-5 py-3 border rounded-2xl outline-none focus:ring-2 transition-all ${newContentFormErrors.title ? "border-red-500 bg-red-50" : "border-gray-200 bg-gray-50"}`} 
                      style={{ "--tw-ring-color": newContentFormErrors.title ? "#ef4444" : emerald600 }} 
                      placeholder="Enter content title" 
                    />
                    {newContentFormErrors.title && <p className="mt-1 text-sm text-red-600">Title is required</p>}
                  </div>

                  {/* DESCRIPTION FIELD WITH JODIT EDITOR */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <div className={`border rounded-2xl overflow-hidden ${newContentFormErrors.description ? "border-red-500" : "border-gray-200"}`}>
                      <div className="jodit-editor-container">
                        <JoditEditor
                          ref={addEditorRef}
                          value={newContentForm.description}
                          config={editorConfig}
                          onChange={handleNewContentEditorChange}
                          onBlur={(newContent) => {
                            if (newContent !== newContentForm.description) {
                              handleNewContentEditorChange(newContent);
                            }
                          }}
                        />
                      </div>
                    </div>
                    {newContentFormErrors.description && <p className="mt-1 text-sm text-red-600">Description is required</p>}
                  </div>
                
                  {/* ACTION BUTTONS */}
                  <div className="flex gap-3 pt-6">
                    <button 
                      type="button" 
                      onClick={closeAddContentModal}
                      className="flex-1 py-3 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-all text-lg font-medium"
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      onClick={handleAddContent} 
                      disabled={addingContent}
                      style={{ backgroundColor: emerald600 }}
                      className="flex-1 py-3 text-white rounded-2xl font-bold hover:brightness-110 transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addingContent ? "Adding..." : "Add Content"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Check if content exists before rendering existing content page
  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 pt-48">
        <div className="text-gray-500 text-2xl mb-4">📄</div>
        <p className="text-gray-600 text-xl mb-4">Content not found</p>
        <button 
          onClick={() => navigate("/About-Dla/BecomeMember")} 
          className="px-6 py-3 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
        >
          Back to Posts
        </button>
      </div>
    );
  }

  // Existing content page - only rendered when content exists
  return (
    <div className="flex flex-col min-h-screen w-full bg-gray-50 pt-16"> {/* Changed: pt-16 instead of mt-20 */}
      {/* Header Section */}
      <div className="px-8 py-6 bg-white border-b">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div className="text-left">
            <h1 className="text-3xl font-bold text-gray-900">Content Details</h1>
            <p className="text-gray-600 mt-1">Content Page</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/About-Dla/BecomeMember")}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors gap-2"
            >
              <ArrowLeft className="w-5 h-5" /> Back to Posts
            </button>
            
            <button 
              onClick={() => setShowEditModal(true)}
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors gap-2"
            >
              <Pencil className="w-5 h-5" /> Edit Content
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-8 md:px-12 py-8 w-full flex-grow"> {/* Changed: py-8 instead of mt-8, added flex-grow */}
        <div className="bg-white rounded-xl shadow-sm p-6 h-full">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="text-emerald-600 text-2xl">📄</div>
              <h2 className="text-2xl font-bold text-gray-900">Content Details</h2>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <Calendar size={18} />
              <span className="font-medium">{formatDate(content.created_at)}</span>
            </div>
          </div>

          {/* Content Details */}
          <div className="space-y-8">
            <div className="border-l-4 pl-6" style={{ borderColor: "#059669" }}>
              <h1 className="text-4xl font-bold text-gray-900 tracking-tight">
                {content.title}
              </h1>
            </div>

            <div className="mt-8 border-t border-gray-200 pt-8">
              <div className="prose prose-lg max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed custom-description" 
                  dangerouslySetInnerHTML={{ __html: content.description }} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Content Modal */}
      {showEditModal && content && (
        <>
          <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={closeEditModal} />
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl pointer-events-auto animate-modalIn border border-gray-100" onClick={(e) => e.stopPropagation()}>
              <div className="px-8 py-5 border-b flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800">Edit Content</h2>
                <button onClick={closeEditModal} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>
              <div className="p-8 space-y-6 overflow-y-auto max-h-[80vh]">
                {/* Title Field */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input 
                    name="title"
                    value={editForm.title}
                    onChange={handleEditChange}
                    className="w-full px-5 py-3 border border-gray-200 rounded-2xl outline-none focus:ring-2 focus:ring-emerald-600 bg-gray-50 transition-all"
                    placeholder="Enter content title"
                  />
                </div>

                {/* Description Field with Jodit Editor */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wider">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <div className="border border-gray-200 rounded-2xl overflow-hidden">
                    <div className="jodit-editor-container">
                      <JoditEditor
                        ref={editEditorRef}
                        value={editForm.description}
                        config={editorConfig}
                        onChange={handleEditEditorChange}
                        onBlur={(newContent) => {
                          if (newContent !== editForm.description) {
                            handleEditEditorChange(newContent);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-6">
                  <button 
                    type="button" 
                    onClick={closeEditModal}
                    className="flex-1 py-3 border border-gray-300 rounded-2xl hover:bg-gray-50 transition-all text-lg font-medium"
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    onClick={handleSaveEdit}
                    disabled={saving}
                    style={{ backgroundColor: "#059669" }}
                    className="flex-1 py-3 text-white rounded-2xl font-bold hover:brightness-110 transition-all text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(10px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes modalIn { 
          from { opacity: 0; transform: scale(0.95); } 
          to { opacity: 1; transform: scale(1); } 
        }
        .animate-fadeIn { animation: fadeIn 0.6s ease-out forwards; }
        .animate-modalIn { animation: modalIn 0.4s ease-out forwards; }
        
        /* Remove background and borders from rich text output */
        .custom-description * { background-color: transparent !important; border: none !important; }
        .custom-description ul { list-style-type: disc; padding-left: 1.5rem; }
        .custom-description ol { list-style-type: decimal; padding-left: 1.5rem; }
        
        /* Jodit Editor Custom Styling */
        .jodit-editor-container :global(.jodit-container) {
          border: none !important;
          background: #f9fafb !important;
          min-height: 300px;
        }
        
        .jodit-editor-container :global(.jodit-workplace) {
          background: #f9fafb !important;
          min-height: 250px;
        }
        
        .jodit-editor-container :global(.jodit-toolbar__box) {
          background: #f9fafb !important;
          border-bottom: 1px solid #eee !important;
        }
        
        .jodit-editor-container :global(.jodit-wysiwyg) {
          background: #f9fafb !important;
          padding: 16px !important;
          min-height: 200px;
        }
      `}</style>
    </div>
  );
}

export default ContentDetailPage;